import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import maestroIcon from '../assets/icons/Maestro-Favicon.png';

const STRIPE_PK = 'pk_live_51T2K4uR640qUzVcQTUdhZY2OLQIhpBuVsG8TMElF0nNfNY8HNoStadhhnRKG6qtqIVEDG4o6KxhvtRSBdQXeQuSP00vtO6RbMU';
const stripePromise = loadStripe(STRIPE_PK);

function trialEndDate() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function StripeCheckoutSlideout({ open, onClose, clientSecret, showOnboarding = false, isResubscribe = false }) {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      const timer = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleOverlayClick = useCallback(() => onClose?.(), [onClose]);

  if (!shouldRender) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 199999 }}>
      <div
        onClick={handleOverlayClick}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      <div
        style={{
          position: 'absolute', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: 480,
          background: '#ffffff',
          borderRadius: '20px 0 0 20px',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.35)',
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #C9A84C, #E8C94A, #C9A84C)',
            padding: '18px 20px', paddingTop: 'max(18px, env(safe-area-inset-top, 18px))',
            display: 'flex', alignItems: 'center', gap: 12,
            position: 'relative', flexShrink: 0,
          }}
        >
          <img src={maestroIcon} alt="" style={{ width: 36, height: 36, borderRadius: 8 }} />
          <span style={{ fontWeight: 800, fontSize: 16, color: '#1a1a2e', letterSpacing: '-0.01em' }}>
            {isResubscribe ? 'Subscribe to Maestro' : 'Start Your Free Trial'}
          </span>
          <button
            onClick={() => onClose?.()}
            style={{
              position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(26,26,46,0.12)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(26,26,46,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(26,26,46,0.12)'}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#1a1a2e" strokeWidth="2.2" strokeLinecap="round">
              <line x1="1" y1="1" x2="13" y2="13" /><line x1="13" y1="1" x2="1" y2="13" />
            </svg>
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {/* Onboarding message — new users only */}
          {showOnboarding && !isResubscribe && (
            <div style={{
              padding: '24px 24px 0',
              borderBottom: '1px solid #f0f0f0',
              paddingBottom: 20,
            }}>
              <h2 style={{
                fontSize: 20, fontWeight: 800, color: '#1a1a2e',
                margin: '0 0 8px', letterSpacing: '-0.02em',
              }}>
                Welcome to Maestro
              </h2>
              <p style={{
                fontSize: 14, lineHeight: 1.6, color: '#666', margin: 0,
              }}>
                Start your 30-day free trial today. Your card won&rsquo;t be charged until your trial ends on{' '}
                <strong style={{ color: '#333' }}>{trialEndDate()}</strong>.
                {' '}Cancel anytime — no questions asked.
              </p>
            </div>
          )}

          {/* Trial reassurance — new users only */}
          {!isResubscribe && (
            <div style={{
              background: '#FFF8E7',
              padding: '14px 20px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              borderBottom: '1px solid #f0e8d0',
              flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#7a6a3a', margin: 0 }}>
                Your card will not be charged today. Your 30-day free trial begins now — billing starts <strong style={{ color: '#5a4d2a' }}>{trialEndDate()}</strong>. Cancel anytime.
              </p>
            </div>
          )}

          {/* Resubscribe reassurance — returning users */}
          {isResubscribe && (
            <div style={{
              background: '#FFF8E7',
              padding: '14px 20px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              borderBottom: '1px solid #f0e8d0',
              flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
              </svg>
              <p style={{ fontSize: 12.5, lineHeight: 1.55, color: '#7a6a3a', margin: 0 }}>
                Welcome back! Your personal music valet remembers you. Subscribe and pick up right where you left off.
              </p>
            </div>
          )}

          {clientSecret ? (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: showOnboarding ? 200 : '100%' }}>
              <div style={{ width: 32, height: 32, border: '3px solid #e5e5e5', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
